import { Children, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from "react";
import { useWriteLock } from "./WriteLock.tsx";

/**
 * The one button.
 *
 * Modern skeuo (chosen 2026-10-28; the rule is in AGENTS.md, "Buttons"), after the buildkite.com
 * hero button: a straight light-to-deep fill, a dark tinted 1px ring, a light line under the top
 * edge, a dark line at the bottom and soft stacked drops. Hover is 10% brighter; pressed keeps only
 * the ring and is 10% darker, with no movement. The recipe lives once, in `styles.css` (`.sk-btn`
 * for a coloured button, `.sk-btn-alt` for the white secondary); a variant only names its colour in
 * `--sk-p` and its text colour. White text passes WCAG AA at the text line for every variant, at
 * rest, on hover and pressed (`packages/shared/src/skeuo.test.ts`).
 *
 * The minimum height is 48px for `md` and `lg`: this app is used with a thumb on a phone. `sm` (36px)
 * is only for commands inside a dense desktop tool, such as the timetable editor (1024px and up).
 */

type Variant = "primary" | "secondary" | "ghost" | "danger" | "confirmed" | "whatsapp" | "freeze";
type Size = "xs" | "sm" | "md" | "lg";

const VARIANTS: Record<Variant, string> = {
  /** The brand colour of the area (blue, the owner navy, the admin dark). */
  primary: "sk-btn [--sk-p:var(--color-accent)] text-inverse border-transparent",
  secondary: "sk-btn-alt border-transparent",
  /** Pure text, no skeu: "Resend", "Use email instead", links that act. */
  ghost: "bg-transparent text-accent-strong border-transparent active:bg-accent-surface",
  /**
   * Cancelling and removing, in the danger red with white text (decided 2026-10-02). Put it only
   * where the action is the point of the screen (a confirm panel for "Cancel the session"), with
   * the safe choice next to it as `secondary`.
   */
  danger: "sk-btn [--sk-p:var(--color-cancelled)] text-inverse border-transparent",
  /** "Yes, came" on the attendance cards: the answer that a participant was there (2026-10-04). */
  confirmed: "sk-btn [--sk-p:var(--color-confirmed)] text-inverse border-transparent",
  /**
   * "Continue with WhatsApp". WhatsApp's own deep teal green, not the bright #25D366: white text on
   * #25D366 is 2.0:1, on #00735E 5.8:1.
   */
  whatsapp: "sk-btn [--sk-p:var(--color-whatsapp)] text-white border-transparent",
  /** "Freeze all" (D17): the primary action of a pause, in its own ice blue, never the brand blue. */
  freeze: "sk-btn [--sk-p:var(--color-freeze)] text-inverse border-transparent",
};

const SIZES: Record<Size, string> = {
  /**
   * A one-glyph command inside a chip (the × that takes a label off a lead in Control Center).
   * Desktop tools only; use it with `ghost` and an `aria-label`.
   */
  xs: "min-h-6 min-w-6 px-1 text-[13px] leading-none",
  /** Commands inside a dense tool (the timetable's note, bar and rail): the same recipe, smaller. */
  sm: "min-h-9 px-3 text-[13px]",
  md: "min-h-12 px-4 text-[15px]",
  lg: "min-h-13 px-5 text-base",
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  /**
   * Not a write, so it stays on in read-only Desk (`WriteLock`): a `primary` that only opens a page,
   * such as "Take attendance". Without it, `primary` and `danger` turn off under a lock.
   */
  readOnlySafe?: boolean;
  children: ReactNode;
};

/** The variants that write. A `WriteLock` turns these off. */
const WRITE_VARIANTS = new Set<Variant>(["primary", "danger", "freeze"]);

/**
 * `disabled` dims the button to 45%: it cannot be used, and the dim says so. `loading` does not
 * dim: the action is already on its way, so the fill and the words stay at full strength (white on
 * a 45% fill fell far below 4.5:1) and the spinner says "wait". Both ignore taps.
 */
type State = "ready" | "loading" | "disabled";

const STATES: Record<State, string> = {
  ready: "",
  loading: "pointer-events-none cursor-progress",
  disabled:
    "disabled:opacity-45 disabled:pointer-events-none aria-disabled:opacity-45 aria-disabled:pointer-events-none",
};

function classesFor(variant: Variant, size: Size, fullWidth: boolean, className: string, state: State): string {
  return [
    "relative inline-flex items-center justify-center gap-2 rounded-lg border font-medium select-none",
    STATES[state],
    VARIANTS[variant],
    SIZES[size],
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  readOnlySafe = false,
  disabled,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  const lock = useWriteLock();
  const locked = lock !== null && !readOnlySafe && WRITE_VARIANTS.has(variant);
  if (locked) {
    disabled = true;
    rest.title = lock;
  }
  return (
    <button
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      // Loading wins over disabled: a save button is often both while its request runs (`disabled`
      // from the form check, `loading` from the request), and it must look busy, not unusable.
      className={classesFor(variant, size, fullWidth, className, loading ? "loading" : disabled ? "disabled" : "ready")}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {translatableText(children)}
    </button>
  );
}

export type ButtonLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  /** Not ready yet: shown with a spinner at full strength, and does nothing when tapped. */
  loading?: boolean;
  children: ReactNode;
};

/**
 * The same button as a real link, for an action that has to be a link: "Continue with WhatsApp"
 * opens wa.me, and a phone only hands a tap on a link straight to the WhatsApp app.
 */
export function ButtonLink({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  href,
  className = "",
  children,
  ...rest
}: ButtonLinkProps) {
  const ready = Boolean(href) && !loading;
  return (
    <a
      href={ready ? href : undefined}
      role={ready ? undefined : "link"}
      aria-disabled={ready ? undefined : true}
      aria-busy={loading || undefined}
      className={classesFor(variant, size, fullWidth, className, loading ? "loading" : ready ? "ready" : "disabled")}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {translatableText(children)}
    </a>
  );
}

/**
 * Each run of text in its own `<span>`, keyed by the words.
 *
 * Page translation swaps text nodes for its own elements, so React can no longer change or remove
 * the text of a button ("Open WhatsApp" that becomes "Open WhatsApp and tap Send"). With the key, a
 * new label is a new span: React removes the old span, which is still a child of the button, and the
 * translator sees a new element to translate. Next to an icon, a span is the same flex item as the
 * bare text was. The crash itself is stopped by `guardDomAgainstTranslation` (lib/translate-guard.ts).
 */
export function translatableText(children: ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  let text = "";
  const flush = () => {
    if (text === "") return;
    out.push(<span key={`text:${out.length}:${text}`}>{text}</span>);
    text = "";
  };
  for (const child of Children.toArray(children)) {
    if (typeof child === "string" || typeof child === "number") {
      text += String(child);
      continue;
    }
    flush();
    out.push(child);
  }
  flush();
  return out;
}

/** The small spinner of a busy button. AI Assistant voice uses it too. */
export function Spinner() {
  return (
    <span
      className="inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent opacity-70"
      aria-hidden="true"
    />
  );
}
